// Axios client + checkout/auth helpers. Single source of API truth.

import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
if (!BASE) {
    // eslint-disable-next-line no-console
    console.error("REACT_APP_BACKEND_URL is not set");
}

export const api = axios.create({
    baseURL: `${BASE}/api`,
    withCredentials: true, // send tca_session cookie
    headers: { "Content-Type": "application/json" },
});

// Optional Bearer fallback — Stripe success page hits /auth/me cross-tab
// so cookie may not always be authoritative; we mirror to localStorage.
const TOKEN_KEY = "tca_token";

export function setStoredToken(token) {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
}
export function getStoredToken() {
    return localStorage.getItem(TOKEN_KEY) || null;
}

api.interceptors.request.use((config) => {
    const tok = getStoredToken();
    if (tok && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${tok}`;
    }
    return config;
});

// ---- Auth ----
export async function apiSignup(email, password) {
    const { data } = await api.post("/auth/signup", { email, password });
    setStoredToken(data.token);
    return data;
}
export async function apiLogin(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    setStoredToken(data.token);
    return data;
}
export async function apiLogout() {
    try { await api.post("/auth/logout"); } catch (_) {}
    setStoredToken(null);
}
export async function apiMe() {
    const { data } = await api.get("/auth/me");
    return data.user;
}

// ---- Checkout ----
export async function apiCreateCheckoutSession(payload) {
    const { data } = await api.post("/checkout/create-session", payload);
    return data; // { url, session_id }
}

// ---- Orders ----
export async function apiGetOrderBySession(sessionId) {
    const { data } = await api.get(`/orders/by-session/${sessionId}`);
    return data; // { order, user }
}

// ---- Portal state + progress ----
export async function apiGetPortalState() {
    const { data } = await api.get("/portal/state");
    return data;
}

export async function apiPatchProgress(payload) {
    const { data } = await api.patch("/portal/progress", payload);
    return data;
}

export async function apiCompleteModule(letter) {
    const { data } = await api.post(`/portal/module/${letter}/complete`);
    return data;
}

// ---- Workspace files ----
export async function apiWorkspaceFiles(moduleLetter) {
    const params = moduleLetter ? { module: moduleLetter } : {};
    const { data } = await api.get("/workspace/files", { params });
    return data; // { files, count }
}

export async function apiWorkspaceUsage() {
    const { data } = await api.get("/workspace/usage");
    return data;
}

export async function apiWorkspaceUpload(moduleLetter, file, onProgress) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("module_letter", moduleLetter);
    const { data } = await api.post("/workspace/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
    });
    return data;
}

export async function apiWorkspaceDelete(fileId) {
    const { data } = await api.delete(`/workspace/file/${fileId}`);
    return data;
}

export function workspaceDownloadUrl(fileId) {
    return `${BASE}/api/workspace/file/${fileId}/download`;
}

export function systemDownloadUrl() {
    return `${BASE}/api/system/download`;
}

// ---- Projects ----
export async function apiProjectsList(status) {
    const params = status ? { status } : {};
    const { data } = await api.get("/projects", { params });
    return data;
}
export async function apiProjectCreate(payload) {
    const { data } = await api.post("/projects", payload);
    return data;
}
export async function apiProjectGet(id) {
    const { data } = await api.get(`/projects/${id}`);
    return data;
}
export async function apiProjectUpdate(id, updates) {
    const { data } = await api.patch(`/projects/${id}`, updates);
    return data;
}
export async function apiProjectArchive(id) {
    const { data } = await api.delete(`/projects/${id}`);
    return data;
}
export async function apiProjectNote(id, note) {
    const { data } = await api.post(`/projects/${id}/note`, { note });
    return data;
}
export async function apiProjectMilestone(id, name) {
    const { data } = await api.post(`/projects/${id}/milestone`, { name });
    return data;
}
export async function apiProjectAttachFile(id, file_id) {
    const { data } = await api.post(`/projects/${id}/attach-file`, { file_id });
    return data;
}

// ---- Stake ----
export async function apiStakeStatus() {
    const { data } = await api.get("/stake/status");
    return data;
}
export async function apiStakeJobs() {
    const { data } = await api.get("/stake/jobs");
    return data;
}
export async function apiStakeSubmitRender(spec = { type: "render", params: {} }) {
    const { data } = await api.post("/stake/render-job", spec);
    return data;
}


// ---- Curriculum ----
export async function apiGetCurriculumList() {
    const { data } = await api.get("/curriculum/");
    return data;
}
export async function apiGetCurriculumModule(letter) {
    const { data } = await api.get(`/curriculum/${letter.toUpperCase()}`);
    return data;
}
export async function apiGetCurriculumLesson(letter, lessonId) {
    const { data } = await api.get(`/curriculum/${letter.toUpperCase()}/lesson/${lessonId}`);
    return data;
}
